import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { GlobalGraphHeader } from 'src/app/appLayout/components/globalHeader/globalGraphHeader/GlobalGraphHeader';
import { GlobalHeader } from 'src/app/appLayout/components/globalHeader/GlobalHeader';
import { GraphNav } from 'src/components/appNav/graphNav/GraphNav';
import { ErrorBoundary } from 'src/components/common/errorBoundary/ErrorBoundary';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { Toasts } from 'src/components/toast/Toast';
import { useAppSettings } from 'src/hooks/useAppSettings';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { ThemeProvider } from '../../../components/themeProvider/ThemeProvider';
import { STUDIO_UI_EXPLORER_FLAGS } from '../explorerPage/explorerBehavioralFlags';
import {
  sandboxCheckRouteConfig,
  sandboxDiffRouteConfig,
  sandboxExplorerRouteConfig,
  sandboxSchemaRouteConfig,
} from '../routes';

import { STUDIO_UI_GRAPH_FLAGS } from './graphBehavioralFlags/graphBehavioralFlags';
import { ProtectedGraphArea } from './ProtectedGraphArea';
import { SharedSandboxAndGraphModals } from './VariantLayoutModals';
import { VariantLayoutProviders } from './VariantLayoutProviders';

const ExplorerPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "ExplorerPage" */ '../explorerPage/ExplorerPage'
    )
  ).ExplorerPage,
}));

const DocumentationPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "DocumentationPage" */ '../documentationPage/DocumentationPage'
    )
  ).DocumentationPage,
}));

const SandboxDiffPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "SandboxDiffPage" */ '../sandboxDiffPage/SandboxDiffPage'
    )
  ).SandboxDiffPage,
}));

const SandboxCheckPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "SandboxCheckPage" */ '../sandboxCheckPage/SandboxCheckPage'
    )
  ).SandboxCheckPage,
}));

export const SandboxLayout = () => {
  const [currentAccountId] = useCurrentAccountId();

  const { data: userData, loading: userLoading } = useQuery(
    gql<GraphQLTypes.SandboxLayout, GraphQLTypes.SandboxLayoutVariables>`
      query SandboxLayout {
        me {
          id
        }
      }
    `,
    { context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }) },
  );

  // TODO: This should be different initially depending on the route. For
  // example, landing on studio cold should set `collapsed` to true.
  const { navCollapsed, setNavCollapsed } = useAppSettings();
  const me = userData?.me;

  if (userLoading) {
    return <Loading />;
  }

  return (
    <VariantLayoutProviders
      graphRef={null}
      graphBehavioralFlags={STUDIO_UI_GRAPH_FLAGS}
    >
      <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden text-primary">
        <GlobalHeader
          leftNavContext={navCollapsed ? 'collapsed' : 'expanded'}
          goHomeLink={undefined}
        >
          <GlobalGraphHeader
            key="Sandbox"
            graphBehavioralFlags={STUDIO_UI_GRAPH_FLAGS}
          />
        </GlobalHeader>

        <div className="flex h-full flex-1 overflow-y-auto">
          <Toasts />
          <GraphNav collapsed={navCollapsed} setCollapsed={setNavCollapsed} />
          <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
            <DocumentTitle title="Sandbox">
              <ErrorBoundary>
                <Switch>
                  <Route
                    path={sandboxSchemaRouteConfig.definition}
                    render={() => (
                      <ProtectedGraphArea accountId={null}>
                        <DocumentTitle title="Schema" />
                        <DocumentationPage />
                      </ProtectedGraphArea>
                    )}
                  />

                  <Route
                    path={sandboxExplorerRouteConfig.definition}
                    render={() => {
                      return (
                        /**
                         * TODO(dark-mode)
                         * We need to wrap Explorer in its own ThemeProvider right now because it's
                         * the only part of the app that publicly supports Dark Mode.
                         * When we're ready to globally roll out dark mode, we will no longer need
                         * this extra wrapping because Explorer should fall back to the ThemeProvider
                         * wrapped around the entire app.
                         *
                         * Steps to roll out dark mode:
                         *     1. Update logic that holds Explorer's dark mode preference to control
                         *        the global app dark mode instead.
                         *     2. Move this control out of Explorer's page settings and into a global
                         *        settings space, likely top nav or User Settings.
                         *     2. Figure out what to do with the ThemeProviders wrapped around other
                         *        things in the app like Embedded Explorer.
                         *     3. Test LD feature flag thoroughly.
                         */
                        <ProtectedGraphArea accountId={null}>
                          <DocumentTitle title="Explorer" />
                          <ThemeProvider className="size-full">
                            <ExplorerPage
                              userId={me?.id}
                              explorerBehavioralFlags={STUDIO_UI_EXPLORER_FLAGS}
                              graphBehavioralFlags={STUDIO_UI_GRAPH_FLAGS}
                            />
                          </ThemeProvider>
                        </ProtectedGraphArea>
                      );
                    }}
                  />
                  <Route
                    path={sandboxDiffRouteConfig.definition}
                    render={() => {
                      return (
                        <>
                          <DocumentTitle title="Schema Diff" />
                          <SandboxDiffPage />
                        </>
                      );
                    }}
                  />
                  <Route
                    path={sandboxCheckRouteConfig.definition}
                    render={() => {
                      return (
                        <>
                          <DocumentTitle title="Checks" />
                          <SandboxCheckPage />
                        </>
                      );
                    }}
                  />

                  <Route component={NotFound} />
                </Switch>
              </ErrorBoundary>
            </DocumentTitle>
            <SharedSandboxAndGraphModals
              currentAccountId={currentAccountId}
              graphBehavioralFlags={STUDIO_UI_GRAPH_FLAGS}
            />
          </div>
        </div>
      </div>
    </VariantLayoutProviders>
  );
};
